import { Model } from '../../hooks/useModelState';
import { PCNode } from '../Nodes';
import PCRoute from '../Route/PCRoute';
import { returnShortestRoute } from '../Route/utils';

export const createRoutesConstraintFilter = (
    constraintNodeIds: string[],
    constraintSpoolIds: string[] = [],
) => (routes: Record<string, PCRoute>) => {
    if (!constraintNodeIds.length && !constraintSpoolIds.length) return routes;
    const requiredNodes = new Set(constraintNodeIds);
    const requiredSpools = new Set(constraintSpoolIds);
    const filtered: Record<string, PCRoute> = {};
    Object.entries(routes).forEach(([id, route]) => {
        const nodeIdsInRoute = new Set(route.routePoints?.map((p) => p.point.id) || []);
        const spoolIdsInRoute = new Set(route.spools?.map((s) => s.spoolId) || []);
        let ok = true;
        requiredNodes.forEach((req) => {
            if (!nodeIdsInRoute.has(req)) ok = false;
        });
        requiredSpools.forEach((req) => {
            if (!spoolIdsInRoute.has(req)) ok = false;
        });
        if (ok) filtered[id] = route;
    });
    return filtered;
};

export const createNodesRouteFilter = (
    nodes: Record<string, PCNode>,
    model: Model,
    stage: 0 | 1 | 2,
    routesOverride?: Record<string, PCRoute>,
): Record<string, PCNode> => {
    if (stage === 0) return {};

    const sourceRoutes = routesOverride ?? model.routes;
    const nodeIdsOnSelectedRoutes = new Set<string>();

    const addRoutePoints = (route: PCRoute) => {
        route.routePoints?.forEach((rp) => nodeIdsOnSelectedRoutes.add(rp.point.id));
    };

    switch (stage) {
        case 1: {
            Object.values(sourceRoutes).forEach(addRoutePoints);
            break;
        }
        case 2: {
            const shortest = returnShortestRoute(sourceRoutes);
            if (shortest) addRoutePoints(shortest);
            break;
        }
        default:
            break;
    }

    const filtered: Record<string, PCNode> = {};
    const selectedStopeNodeId = model.selectedStope?.originNode?.id;

    Object.values(nodes).forEach((node) => {
        if (nodeIdsOnSelectedRoutes.has(node.id)) {
            if (node.isStope && selectedStopeNodeId && node.id !== selectedStopeNodeId) {
                return;
            }
            filtered[node.id] = node;
        }
    });

    return filtered;
};

export const shortestRouteFilter = (routes: Record<string, PCRoute>): Record<string, PCRoute> => {
    const entries = Object.entries(routes);
    if (entries.length === 0) return {};

    const [shortestId, shortest] = entries.reduce((acc, curr) => {
        const [, minRoute] = acc;
        const [, route] = curr;
        return route.routeLength < minRoute.routeLength ? curr : acc;
    });

    return { [shortestId]: shortest };
};

export const selectedRouteFilter = (routes: Record<string, PCRoute>): Record<string, PCRoute> => {
    const selected = Object.values(routes).find((route) => route.selected);
    if (!selected) {
        return shortestRouteFilter(routes);
    }

    const selectedId = selected.originNode?.id;
    if (!selectedId) return shortestRouteFilter(routes);

    return { [selectedId]: selected };
  };
